import { useMemo } from 'react';
import IAccreditationApplicationForm from '../../../../interfaces/IAccreditationApplicationForm';
import IApplicationRequirements, {
    IAccreditationApplicationRequirements,
    IAppMultipleChoiceRequirement,
    IAppRequirement,
} from '../../../../interfaces/IApplicationRequirements';
import ISimpleNameValuePair from '../../../../interfaces/ISimpleNameValuePair';
import ISupervisor from '../../../../interfaces/ISupervisor';
import IClinicalContact from '../../../../interfaces/IClinicalContact';
import IActivity from '../../../../interfaces/IActivity';
import { AccreditationType } from '../../../../graphql/__generated__/graphql';

interface Props {
    accreditationType?: AccreditationType;
    isRenewal?: boolean;
    hasMitigation?: boolean;
}

export const useApplicationValidator = ({
    accreditationType,
    isRenewal = false,
    hasMitigation = false,
}: Props) => {
    const accreditationTypeRequirements = useMemo(() => {
        const baseRequirements: IApplicationRequirements = JSON.parse(
            accreditationType?.requirementsJson || '{}'
        );
        return isRenewal ? baseRequirements.reaccreditation : baseRequirements.accreditation;
    }, [accreditationType, isRenewal]);

    const isCA = () => {
        return Boolean(
            !isRenewal &&
                (accreditationTypeRequirements as IAccreditationApplicationRequirements).config
                    ?.isCA
        );
    };

    const validateNumberField = (requirement: IAppRequirement, value?: number) => {
        // @ts-ignore
        if (value === undefined || value === '')
            return requirement.required ? 'is a required field' : undefined;
        if (value < (requirement.min || 0)) return 'is below the minimum.';
        if (value > (requirement.max || Number.MAX_SAFE_INTEGER)) return 'is above the maximum.';
    };

    const validateMultipleChoice = (
        requirement: IAppMultipleChoiceRequirement,
        value: string[]
    ) => {
        const minSafe = requirement.minChoices || 0;
        const maxSafe = requirement.maxChoices || Number.MAX_SAFE_INTEGER;
        if (value.length < minSafe) return `requires ${minSafe - value.length} more selection(s).`;
        if (value.length > maxSafe) return `has ${value.length - maxSafe} too many selections.`;
    };

    const validateField = (
        requirement: IAppRequirement,
        actual?: ISimpleNameValuePair
    ): string | undefined => {
        switch (requirement.type) {
            case 'number':
                return validateNumberField(requirement, actual?.value);
            case 'checkbox':
                return requirement.required && !actual?.value ? 'is a required field' : undefined;
            case 'document':
                return requirement.required && !actual?.value
                    ? 'is a required document'
                    : undefined;
            case 'multipleChoice':
                return !actual?.value || !Array.isArray(actual?.value)
                    ? 'is not valid'
                    : validateMultipleChoice(
                          requirement as IAppMultipleChoiceRequirement,
                          actual.value
                      );
        }
    };

    const validateExperience = (applicationForm: IAccreditationApplicationForm): string[] => {
        if (!accreditationTypeRequirements?.experience) return [];
        // @ts-ignore
        return accreditationTypeRequirements.experience
            .filter((x) => x.required)
            .map((expReq) => {
                const value = applicationForm.experiences?.find((exp) => exp.name === expReq.name);
                if (hasMitigation && (value?.value === '' || value?.value === false)) {
                    return `${expReq.title} is a required field`;
                } else if (!hasMitigation) {
                    const errorMessage = validateField(expReq, value);
                    return errorMessage ? `${expReq.title} ${errorMessage}` : undefined;
                } else return undefined;
            })
            .filter(Boolean);
    };

    const validateSupervisor = (supervisor: ISupervisor): string[] => {
        const errors = [];
        if (!supervisor.ongoing && !supervisor.endDate)
            errors.push(`${supervisor.name} requires an end date or should be set as ongoing`);
        if (!supervisor.startDate) errors.push(`${supervisor.name} requires a start date`);
        return errors;
    };

    const validateSupervisors = (applicationForm: IAccreditationApplicationForm): string[] => {
        if (!applicationForm.supervisors?.find((s) => s.primary))
            return ['You must specify at least one primary supervisor'];
        return applicationForm.supervisors.flatMap(validateSupervisor);
    };

    const validateClinicalContact = (clinicalContact: IClinicalContact): boolean => {
        return !!(
            clinicalContact.initials?.length &&
            clinicalContact.treatmentSetting?.length &&
            clinicalContact.supervisor?.id &&
            clinicalContact.startDate &&
            clinicalContact.numberOfSessions &&
            clinicalContact.presentingProblem?.length &&
            (!isCA() || !!clinicalContact.age)
        );
    };

    const validateClinicalContacts = (applicationForm: IAccreditationApplicationForm): string[] => {
        if (!applicationForm.clinicalContacts) return [];
        const clientsRequired = Number(
            applicationForm.experiences?.find((e) => e.name === 'clients-treated')?.value
        );
        const clientCount = applicationForm.clinicalContacts?.length;
        if (clientCount !== clientsRequired)
            return [
                `You need to declare all ${clientsRequired} contacts included in "Your Experience" (${clientsRequired - clientCount} more required)`,
            ];
        const clientErrors = applicationForm.clinicalContacts.map(validateClinicalContact);
        if (clientErrors.filter((r) => !r).length)
            return [
                `${clientErrors.length} ${clientErrors.length === 1 ? 'client is incomplete' : 'clients are incomplete'}`,
            ];
        return [];
    };

    const validateDocuments = (applicationForm: IAccreditationApplicationForm): string[] => {
        if (!accreditationTypeRequirements?.documents?.length) return [];
        const flatDocs = accreditationTypeRequirements.documents?.flatMap(
            (req) => req.requirements
        );
        if (!flatDocs) return [];
        // @ts-ignore
        return flatDocs
            .filter((x) => x.required)
            .map((req) => {
                const matchingDoc = applicationForm.documents?.find((doc) => req.name === doc.name);
                const documentError = validateField(req, matchingDoc);
                return documentError
                    ? `${req.title || 'Minimum qualification confirmation'} ${documentError}`
                    : undefined;
            })
            .filter(Boolean);
    };

    const validateActivity = (activity: IActivity) => {
        return !!(
            activity.eventAndTitle?.trim()?.length &&
            activity.date &&
            activity.speakers?.trim()?.length &&
            activity.provider?.trim()?.length &&
            activity.provider !== '__other__' &&
            validateCredits(activity)
        );
    };

    const validateCredits = (activity: IActivity) => {
        if (!activity.relatedCreditsHours && !activity.specificEmdrCredits) {
            return false;
        }

        return !(activity.relatedCreditsHours && activity.specificEmdrCredits);
    };

    const validateActivities = (applicationForm: IAccreditationApplicationForm): string[] => {
        if (!applicationForm.activities || applicationForm.activities?.length! < 1)
            return ['Activities missing'];
        const activityError = applicationForm.activities?.find(
            (activity) => !validateActivity(activity)
        );
        if (activityError) return ['One or more activities are in error'];

        const specificTotal = applicationForm.activities?.reduce((acc, activity) => {
            const credits = activity.specificEmdrCredits;
            return acc + (credits ? credits : 0);
        }, 0);

        if (specificTotal < 50)
            return ['Not enough specific EMDR credits. ' + (50 - specificTotal) + ' more required'];

        const total = applicationForm.activities?.reduce((acc, activity) => {
            const credits =
                (activity.relatedCreditsHours || 0) + (activity.specificEmdrCredits || 0);
            return acc + (credits ? credits : 0);
        }, 0);

        return total >= 75
            ? []
            : [
                  'Not enough activity credits have been specified: ' +
                      (75 - total) +
                      ' more required',
              ];
    };

    const validateActivityDetails = (applicationForm: IAccreditationApplicationForm): string[] => {
        if (
            applicationForm.activityDetails?.length! <
            (accreditationTypeRequirements.activityDetails?.minLength || 0)
        )
            return ['Activity details requires more information'];
        if (
            applicationForm.activityDetails?.length! >
            (accreditationTypeRequirements.activityDetails?.maxLength || 100000)
        )
            return ['Activity details contains too many characters'];
        return [];
    };

    const validateForm = (applicationForm: IAccreditationApplicationForm) => {
        const specificErrors = isRenewal
            ? [...validateActivities(applicationForm), ...validateActivityDetails(applicationForm)]
            : [
                  ...validateClinicalContacts(applicationForm),
                  ...validateSupervisors(applicationForm),
              ];
        const genericErrors = [
            ...validateDocuments(applicationForm),
            ...validateExperience(applicationForm),
        ];
        return genericErrors.concat(specificErrors);
    };

    return useMemo(
        () => ({
            validateForm,
            validateField,
            validateClinicalContact,
            validateClinicalContacts,
            validateDocuments,
            validateExperience,
            validateSupervisors,
            validateActivity,
            validateActivities,
            validateActivityDetails,
        }),
        [accreditationType, isRenewal, hasMitigation, accreditationTypeRequirements]
    );
};
